import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const FacultyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, studentsRes, placementsRes, interviewsRes, recruitersRes] = await Promise.all([
        api.get('/faculty-dashboard/placement-stats'),
        api.get('/students'),
        api.get('/placements'),
        api.get('/interviews'),
        api.get('/faculty-dashboard/recruiters')
      ]);

      console.log('Stats:', statsRes.data);
      console.log('Students:', studentsRes.data);
      console.log('Placements:', placementsRes.data);
      console.log('Interviews:', interviewsRes.data);
      console.log('Recruiters:', recruitersRes.data);

      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setPlacements(placementsRes.data);
      setInterviews(interviewsRes.data);
      setRecruiters(recruitersRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      alert('Error loading dashboard data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Calculate package statistics from placements
  const getPackageStats = () => {
    if (placements.length === 0) {
      return { highest: 0, average: 0, lowest: 0 };
    }
    
    const salaries = placements.map(p => Number(p.salary));
    return {
      highest: Math.max(...salaries),
      average: stats?.avgPackage || 0,
      lowest: Math.min(...salaries)
    };
  };

  const packageStats = getPackageStats();

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loader}>Loading Dashboard...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>👨‍🏫 Faculty Dashboard</h1>
          <p style={styles.headerSubtitle}>Placement Analytics & Management</p>
        </div>
        <button onClick={handleLogout} style={styles.logoutButton}>
          🚪 Logout
        </button>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>👨‍🎓</div>
          <div style={styles.statNumber}>{stats?.totalStudents || 0}</div>
          <div style={styles.statLabel}>Total Students</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💼</div>
          <div style={styles.statNumber}>{stats?.placedStudents || 0}</div>
          <div style={styles.statLabel}>Placed Students</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💰</div>
          <div style={styles.statNumber}>{stats?.avgPackage || 0}</div>
          <div style={styles.statLabel}>Avg Package (LPA)</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>📊</div>
          <div style={styles.statNumber}>{stats?.placementRate || 0}%</div>
          <div style={styles.statLabel}>Placement Rate</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('overview')}
          style={{
            ...styles.tab,
            ...(activeTab === 'overview' ? styles.activeTab : {}),
          }}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('students')}
          style={{
            ...styles.tab,
            ...(activeTab === 'students' ? styles.activeTab : {}),
          }}
        >
          Students
        </button>
        <button
          onClick={() => setActiveTab('placements')}
          style={{
            ...styles.tab,
            ...(activeTab === 'placements' ? styles.activeTab : {}),
          }}
        >
          Placements
        </button>
        <button
          onClick={() => setActiveTab('interviews')}
          style={{
            ...styles.tab,
            ...(activeTab === 'interviews' ? styles.activeTab : {}),
          }}
        >
          Interviews
        </button>
      </div>

      {/* Tab Content */}
      <div style={styles.content}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>🏆 Top Recruiters</h3>
              {recruiters.length > 0 ? (
                <div style={styles.recruiterGrid}>
                  {recruiters.map((recruiter, index) => (
                    <div key={index} style={styles.recruiterCard}>
                      <div style={styles.recruiterRank}>#{index + 1}</div>
                      <div style={styles.recruiterName}>{recruiter.companyName}</div>
                      <div style={styles.recruiterCount}>{recruiter.location || 'N/A'}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={styles.emptyMessage}>No recruiters added yet</p>
              )}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>📈 Package Statistics</h3>
              <div style={styles.packageStats}>
                <div style={styles.packageCard}>
                  <div style={styles.packageLabel}>Highest Package</div>
                  <div style={styles.packageValue}>₹ {packageStats.highest} LPA</div>
                </div>
                <div style={styles.packageCard}>
                  <div style={styles.packageLabel}>Average Package</div>
                  <div style={styles.packageValue}>₹ {packageStats.average} LPA</div>
                </div>
                <div style={styles.packageCard}>
                  <div style={styles.packageLabel}>Lowest Package</div>
                  <div style={styles.packageValue}>₹ {packageStats.lowest} LPA</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>👨‍🎓 All Students ({students.length})</h3>
            {students.length > 0 ? (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>USN</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Branch</th>
                      <th style={styles.th}>CGPA</th>
                      <th style={styles.th}>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => (
                      <tr key={index}>
                        <td style={styles.td}>{student.name}</td>
                        <td style={styles.td}>{student.usn}</td>
                        <td style={styles.td}>{student.email}</td>
                        <td style={styles.td}>{student.branch}</td>
                        <td style={styles.td}>{student.cgpa}</td>
                        <td style={styles.td}>{student.graduationYear}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={styles.emptyMessage}>No students registered yet</p>
            )}
          </div>
        )}

        {/* Placements Tab */}
        {activeTab === 'placements' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>💼 All Placements ({placements.length})</h3>
            {placements.length > 0 ? (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Student</th>
                      <th style={styles.th}>Company</th>
                      <th style={styles.th}>Position</th>
                      <th style={styles.th}>Salary (LPA)</th>
                      <th style={styles.th}>Joining Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placements.map((placement, index) => (
                      <tr key={index}>
                        <td style={styles.td}>{placement.student}</td>
                        <td style={styles.td}>{placement.companyName}</td>
                        <td style={styles.td}>{placement.jobRole}</td>
                        <td style={styles.td}>{placement.salary}</td>
                        <td style={styles.td}>{new Date(placement.joiningDate).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={styles.emptyMessage}>No placements added yet</p>
            )}
          </div>
        )}

        {/* Interviews Tab */}
        {activeTab === 'interviews' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>🎯 All Interviews ({interviews.length})</h3>
            {interviews.length > 0 ? (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Student</th>
                      <th style={styles.th}>Company</th>
                      <th style={styles.th}>Round</th>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interviews.map((interview, index) => (
                      <tr key={index}>
                        <td style={styles.td}>{interview.student}</td>
                        <td style={styles.td}>{interview.companyName}</td>
                        <td style={styles.td}>{interview.mode}</td>
                        <td style={styles.td}>{new Date(interview.date).toLocaleDateString()}</td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.statusBadge,
                            background: interview.result === 'Selected' ? '#48bb78' :
                                       interview.result === 'Rejected' ? '#f56565' :
                                       interview.result === 'Pending' ? '#ed8936' : '#4299e1'
                          }}>
                            {interview.result}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={styles.emptyMessage}>No interviews scheduled yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '2rem',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  loader: {
    fontSize: '2rem',
    color: 'white',
    fontWeight: '600',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.95)',
    padding: '2rem',
    borderRadius: '15px',
    marginBottom: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  headerTitle: {
    margin: 0,
    color: '#333',
    fontSize: '2rem',
  },
  headerSubtitle: {
    margin: '0.5rem 0 0 0',
    color: '#666',
  },
  logoutButton: {
    background: '#f56565',
    color: 'white',
    border: 'none',
    padding: '0.8rem 1.5rem',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  statCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  statIcon: {
    fontSize: '50px',
    marginBottom: '1rem',
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: '700',
    color: '#333',
    marginBottom: '0.5rem',
  },
  statLabel: {
    color: '#666',
    fontSize: '1rem',
  },
  tabContainer: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  tab: {
    background: 'rgba(255,255,255,0.9)',
    border: 'none',
    padding: '1rem 2rem',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    background: 'white',
    boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
    transform: 'translateY(-2px)',
  },
  content: {
    maxWidth: '100%',
  },
  section: {
    background: 'white',
    borderRadius: '15px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  sectionTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.8rem',
    color: '#333',
  },
  recruiterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
  },
  recruiterCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '1.5rem',
    borderRadius: '10px',
    textAlign: 'center',
  },
  recruiterRank: {
    fontSize: '1.5rem',
    fontWeight: '700',
    marginBottom: '0.5rem',
  },
  recruiterName: {
    fontSize: '1.2rem',
    fontWeight: '600',
    marginBottom: '0.3rem',
  },
  recruiterCount: {
    fontSize: '0.9rem',
    opacity: 0.9,
  },
  packageStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  packageCard: {
    background: '#f7fafc',
    padding: '1.5rem',
    borderRadius: '10px',
    textAlign: 'center',
  },
  packageLabel: {
    color: '#666',
    marginBottom: '0.5rem',
  },
  packageValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#333',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: '#f7fafc',
    padding: '1rem',
    textAlign: 'left',
    fontWeight: '600',
    color: '#333',
    borderBottom: '2px solid #e2e8f0',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    color: '#666',
  },
  statusBadge: {
    padding: '0.3rem 0.8rem',
    borderRadius: '20px',
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '600',
    display: 'inline-block',
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    fontSize: '1.1rem',
    padding: '2rem',
  },
};

export default FacultyDashboard;
